import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { LandingPageDetail, LandingPageInput, GallerySummary, ImageAsset, SiteSummary, createDraftLandingPage } from '../../models/cms-models';
import { LandingPageEditorComponent } from '../../components/landing-page-editor/landing-page-editor';
import { LandingPagesService } from '../../services/landing-pages-service';
import { GalleriesService } from '../../services/galleries-service';
import { ImagesService } from '../../services/images-service';
import { SiteContextService } from '../../services/site-context.service';
import { TagsService } from '../../services/tags-service';

@Component({
  selector: 'app-landing-page-editor-page',
  imports: [CommonModule, RouterLink, LandingPageEditorComponent],
  templateUrl: './landing-page-editor-page.html',
  styleUrl: './landing-page-editor-page.css',
})
export class LandingPageEditorPage implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private landingPagesService = inject(LandingPagesService);
  private imagesService = inject(ImagesService);
  private galleriesService = inject(GalleriesService);
  private siteContext = inject(SiteContextService);
  private tagsService = inject(TagsService);
  private destroyRef = inject(DestroyRef);

  @ViewChild(LandingPageEditorComponent) private editor?: LandingPageEditorComponent;

  sites: SiteSummary[] = [];
  landingPage: LandingPageDetail | null = null;
  siteId: string | null = null;
  loading = false;
  saving = false;
  error: string | null = null;
  isCreateMode = false;
  tagSuggestions: string[] = [];
  availableImages: ImageAsset[] = [];
  availableGalleries: GallerySummary[] = [];
  private activeRouteKey: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.route.paramMap, this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, sites, activeSiteId]) => {
        void this.loadRouteState(params.get('landingPageId'), sites, activeSiteId);
      });
  }

  get pageTitle(): string {
    const title = this.landingPage?.title.trim();
    if (title) {
      return title;
    }

    return this.isCreateMode ? 'New landing page' : 'Landing page';
  }

  get siteName(): string | null {
    return this.sites.find((site) => site.id === this.siteId)?.name ?? null;
  }

  get draft(): LandingPageDetail | null {
    return this.editor?.draft ?? this.landingPage;
  }

  get hasUnsavedChanges(): boolean {
    return this.editor?.hasUnsavedChanges ?? false;
  }

  saveCurrentLandingPage(): void {
    if (!this.editor?.draft || this.saving || !this.hasUnsavedChanges) {
      return;
    }

    void this.saveLandingPage(this.editor.draft);
  }

  async saveLandingPage(landingPage: LandingPageDetail): Promise<void> {
    this.saving = true;
    this.error = null;

    try {
      if (this.isCreateMode) {
        const created = await this.landingPagesService.createLandingPage(this.toInput(landingPage));
        this.landingPage = created;
        this.siteId = created.siteId;
        this.siteContext.setActiveSite(created.siteId);
        void this.router.navigate(['/landing-pages', created.id], { replaceUrl: true });
      } else {
        const updated = await this.landingPagesService.updateLandingPage(landingPage.id, this.toInput(landingPage));
        this.landingPage = updated;
        this.siteId = updated.siteId;
        this.siteContext.setActiveSite(updated.siteId);
      }
    } catch {
      this.error = this.isCreateMode ? 'Failed to create the landing page.' : 'Failed to save the landing page.';
    } finally {
      this.saving = false;
    }
  }

  async deleteLandingPage(landingPageId: string): Promise<void> {
    if (!this.siteId) {
      return;
    }

    const confirmed = window.confirm('Delete this landing page? This cannot be undone.');
    if (!confirmed) {
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      await this.landingPagesService.deleteLandingPage(landingPageId);
      this.siteContext.setActiveSite(this.siteId);
      void this.router.navigate(['/landing-pages']);
    } catch {
      this.error = 'Failed to delete the landing page.';
    } finally {
      this.saving = false;
    }
  }

  private async loadRouteState(
    landingPageId: string | null,
    sites: SiteSummary[],
    activeSiteId: string | null,
  ): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.loading = false;
      this.landingPage = null;
      this.siteId = null;
      this.tagSuggestions = [];
      this.availableImages = [];
      this.availableGalleries = [];
      return;
    }

    const resolvedSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!resolvedSiteId) {
      return;
    }

      const routeKey = landingPageId ? `landing-page:${landingPageId}` : `new:${resolvedSiteId}`;

    if (this.activeRouteKey === routeKey) {
      return;
    }

    this.activeRouteKey = routeKey;
    this.loading = true;
    this.error = null;

    try {
      if (landingPageId) {
        this.isCreateMode = false;
        const landingPage = await this.landingPagesService.getLandingPage(landingPageId);
        this.landingPage = landingPage;
        this.siteId = landingPage.siteId;
        const [tagSuggestions, availableImages, availableGalleries] = await Promise.all([
          this.tagsService.getSiteTags(landingPage.siteId),
          this.imagesService.getSiteImages(landingPage.siteId),
          this.galleriesService.getSiteGalleries(landingPage.siteId),
        ]);
        this.tagSuggestions = tagSuggestions;
        this.availableImages = availableImages;
        this.availableGalleries = availableGalleries;
        this.siteContext.setActiveSite(landingPage.siteId);
      } else {
        this.isCreateMode = true;
        this.siteId = resolvedSiteId;
        this.landingPage = createDraftLandingPage(resolvedSiteId);
        const [tagSuggestions, availableImages, availableGalleries] = await Promise.all([
          this.tagsService.getSiteTags(resolvedSiteId),
          this.imagesService.getSiteImages(resolvedSiteId),
          this.galleriesService.getSiteGalleries(resolvedSiteId),
        ]);
        this.tagSuggestions = tagSuggestions;
        this.availableImages = availableImages;
        this.availableGalleries = availableGalleries;
        this.siteContext.setActiveSite(resolvedSiteId);
      }
    } catch {
      this.activeRouteKey = null;
      this.error = landingPageId ? 'Failed to load the landing page.' : 'Failed to prepare a new landing page.';
      this.landingPage = null;
      this.tagSuggestions = [];
      this.availableImages = [];
      this.availableGalleries = [];
    } finally {
      this.loading = false;
    }
  }

  private toInput(landingPage: LandingPageDetail): LandingPageInput {
    return {
      siteId: landingPage.siteId,
      title: landingPage.title,
      path: landingPage.path,
      metadata: landingPage.metadata,
      tags: [...landingPage.tags],
      publicationState: landingPage.publicationState,
      scheduledPublishAt: landingPage.scheduledPublishAt,
      blocks: landingPage.blocks.map((block) => ({
        ...block,
        referenceLinks: block.referenceLinks.map((reference) => ({ ...reference })),
      })),
    };
  }
}
